import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import type { ClientFormField } from '../components/forms/DynamicClientFields.js'

export interface ClientGroupOption {
  id: string
  name: string
}

export interface UseClientGroupsResult {
  groups: ClientGroupOption[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  fetchFormFields: (groupId: string) => Promise<ClientFormField[]>
}

/** Client-group dropdown + per-group dynamic form fields (GET /admin/client-groups/dropdown, GET /admin/client-groups/:id/form-fields). */
export function useClientGroups(autoLoad = true): UseClientGroupsResult {
  const [groups, setGroups] = useState<ClientGroupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ clientGroups: ClientGroupOption[] }>('/admin/client-groups/dropdown')
      setGroups(res.clientGroups ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load client groups')
      setGroups([])
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchFormFields = useCallback(async (groupId: string): Promise<ClientFormField[]> => {
    const res = await api.get<{ formFields: ClientFormField[] }>(`/admin/client-groups/${groupId}/form-fields`)
    return res.formFields ?? []
  }, [])

  useEffect(() => {
    if (autoLoad) void refetch()
  }, [autoLoad, refetch])

  return { groups, loading, error, refetch, fetchFormFields }
}
